// Upload parts: byte ranges of a file in api.uploads.part_size parts (the last one shorter), RFC 9530 Content-Digest values from WebCrypto sha-256, hex digests, a browser fingerprint of a file (name, size, last modified) that keys a paused upload, and the retry delays of a failed part (0, 1, 3, 5 and 10 s).
export interface PartRange {
  number: number;
  start: number;
  end: number;
}

export const RETRY_DELAYS_MS = [0, 1_000, 3_000, 5_000, 10_000] as const;

export function partRanges(size: number, partSize: number): PartRange[] {
  if (size <= 0 || partSize <= 0) {
    return [];
  }
  const count = Math.ceil(size / partSize);
  return Array.from({ length: count }, (_, index) => ({
    number: index + 1,
    start: index * partSize,
    end: Math.min(size, (index + 1) * partSize),
  }));
}

export function missingParts(ranges: readonly PartRange[], received: readonly number[]): PartRange[] {
  const done = new Set(received);
  return ranges.filter((range) => !done.has(range.number));
}

export function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function toBase64(buffer: ArrayBuffer): string {
  let binary = "";
  for (const byte of new Uint8Array(buffer)) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

export async function sha256(data: ArrayBuffer): Promise<ArrayBuffer> {
  return crypto.subtle.digest("SHA-256", data);
}

export async function contentDigest(data: ArrayBuffer): Promise<string> {
  return `sha-256=:${toBase64(await sha256(data))}:`;
}

export function fingerprint(file: Pick<File, "name" | "size" | "lastModified">): string {
  return `${file.name}:${String(file.size)}:${String(file.lastModified)}`;
}

export function retryDelay(attempt: number): number | null {
  return attempt < RETRY_DELAYS_MS.length ? (RETRY_DELAYS_MS[attempt] ?? null) : null;
}
